import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  Alert,
  RefreshControl,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../src/utils/formatNumber';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootState, AppDispatch } from '../../src/redux/store';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../src/styles/theme';
import { Toast, ToastType } from '../../src/components/common/Toast';
import { ConfirmationModal } from '../../src/components/common/ConfirmationModal';
import {
  toggleItem,
  clearPurchased,
  addCustomItem,
  removeCustomItem,
  clearList,
  removeRecipeItems,
  moveToInventory,
  buildGroceryList,
  selectGroceryWeekStart,
  selectGroceryRange,
  setStale,
} from '../../src/redux/groceryListSlice';
import { GroceryCategory, GroceryItem } from '../../src/types/groceryList';
import { rayonOfItem, type Rayon } from '../../src/components/groceryList/rayonMapping';
import { formatGroceryListForCopy } from '../../src/services/groceryListService';
import { formatQuantity, abbreviateUnit } from '../../src/utils/ingredientParser';
import AddCustomItemModal from '../../src/components/groceryList/AddCustomItemModal';
import AlreadyHaveSection from '../../src/components/groceryList/AlreadyHaveSection';
import SyncStatusIndicator from '../../src/components/groceryList/SyncStatusIndicator';
import { selectAllDeals } from '../../src/redux/dealsSlice';
import { matchIngredientsToDeals } from '../../src/services/weekGeneratorService';
import { selectDefaultFamilySize, excludeStaple, unexcludeStaple } from '../../src/redux/userPreferencesSlice';
import { STAPLE_GROUPS, stapleRank } from '../../src/constants/staples';
import { selectMealsForDateRange } from '../../src/redux/mealPlanSlice';
import { useToday } from '../../src/hooks/useToday';
import { formatWeekdayDay } from '../../src/utils/dateLocale';
import { selectActiveInventory, addItemsToPantryAndSync } from '../../src/redux/inventorySlice';
import { shouldSyncToPantryOnToggle } from '../../src/components/groceryList/groceryCheckOffPantryUtils';
import { trackEvent } from '../../src/services/analyticsService';
import { useLufaDealsEnabled } from '../../src/hooks/useLufaDealsEnabled';
import { HeaderAvatarButton } from '../../src/components/HeaderAvatarButton';

type ViewMode = 'recipe' | 'category' | 'lufa';

const GroceryListScreen: React.FC = () => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const items = useSelector((state: RootState) => state.groceryList?.items ?? []);
  const alreadyHaveItems = useSelector((state: RootState) => state.groceryList?.alreadyHaveItems ?? []);

  const familySize = useSelector(selectDefaultFamilySize);
  // The list belongs to its own pinned week, which is not necessarily the week
  // the planner is showing, and only from today on (#804) — servings must be
  // read from the same days as the items.
  const groceryWeekStart = useSelector(selectGroceryWeekStart);
  const today = useToday();
  const groceryRange = useSelector((state: RootState) => selectGroceryRange(state, today), shallowEqual);
  const groceryWeekMeals = useSelector((state: RootState) =>
    selectMealsForDateRange(state, groceryRange.start, groceryRange.end),
  );
  // Past days were left out: say so, or Monday's recipes just vanish.
  const pastDaysDropped = groceryRange.start > groceryWeekStart;
  const inventory = useSelector(selectActiveInventory);
  const allDeals = useSelector(selectAllDeals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isRegenerating = useSelector((state: RootState) => state.groceryList?.isRegenerating ?? false);
  const lufaEnabled = useLufaDealsEnabled();
  const metrics = useSelector((state: RootState) => state.groceryList?.metrics ?? null);
  const generatedDate = useSelector((state: RootState) => state.groceryList?.generatedDate ?? null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('category');

  useEffect(() => {
    if (!lufaEnabled && viewMode === 'lufa') setViewMode('recipe');
  }, [lufaEnabled, viewMode]);
  // Toast state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<ToastType>('info');

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }, []);

  // Build servings lookup per recipeId from the list's own week
  const servingsByRecipe = useMemo(() => {
    const map: Record<string, number> = {};
    groceryWeekMeals.forEach(meal => {
      if (meal.recipeId && meal.recipeId !== 'free-text') {
        map[meal.recipeId] = (map[meal.recipeId] || 0) + (meal.servings ?? 1);
      }
    });
    return map;
  }, [groceryWeekMeals]);

  // Group items by recipe
  const itemsByRecipe = useMemo(() => {
    const groups: Record<string, { recipeName: string; items: GroceryItem[] }> = {};
    const otherItems: GroceryItem[] = [];

    items.forEach((item: GroceryItem) => {
      if (item.recipeIds.length === 0 || item.isCustom) {
        otherItems.push(item);
      } else {
        // Add to each recipe group (item may belong to multiple recipes)
        item.recipeIds.forEach((recipeId: string, idx: number) => {
          if (!groups[recipeId]) {
            groups[recipeId] = {
              recipeName: item.recipeNames[idx] || 'Unknown Recipe',
              items: [],
            };
          }
          groups[recipeId].items.push(item);
        });
      }
    });

    return { groups, otherItems };
  }, [items]);

  // Group items by French display rayon (aisle) — presentation-level grouping
  // of the internal categories; the enum and persisted data are unchanged.
  const itemsByRayon = useMemo(() => {
    const grouped = new Map<string, { rayon: Rayon; items: GroceryItem[] }>();
    items.forEach((item: GroceryItem) => {
      const rayon = rayonOfItem(item);
      const entry = grouped.get(rayon.key);
      if (entry) {
        entry.items.push(item);
      } else {
        grouped.set(rayon.key, { rayon, items: [item] });
      }
    });
    return [...grouped.values()].sort((a, b) => a.rayon.sortOrder - b.rayon.sortOrder);
  }, [items]);

  // "Auto · il y a X min" seal from the last generation timestamp
  const autoSealLabel = useMemo(() => {
    if (!generatedDate) return null;
    const diffMin = Math.floor((Date.now() - new Date(generatedDate).getTime()) / 60000);
    if (diffMin < 1) return t('groceryList.autoSealNow', "Auto · à l'instant");
    if (diffMin < 60) {
      return t('groceryList.autoSealMin', { defaultValue: 'Auto · il y a {{count}} min', count: diffMin });
    }
    const diffH = Math.floor(diffMin / 60);
    return t('groceryList.autoSealHours', { defaultValue: 'Auto · il y a {{count}} h', count: diffH });
  }, [generatedDate, t]);

  // Derive pantry and staple counts for honest banner
  const pantryCount = alreadyHaveItems.filter(
    i => i.source === 'inventory' || i.source === 'manual-review' || i.source === 'both'
  ).length;
  const stapleCount = alreadyHaveItems.filter(i => i.source === 'staple').length;
  const removedCount = pantryCount + stapleCount; // For backwards compatibility

  const checkedCount = items.filter((i: GroceryItem) => i.isChecked).length;
  const remainingCount = items.length - checkedCount;

  // Group items available at Lufa
  const lufaItems = useMemo(() => {
    if (!allDeals || allDeals.length === 0 || items.length === 0) {
      return [];
    }

    // Get ingredient names from unchecked grocery items
    const uncheckedItems = items.filter((item: GroceryItem) => !item.isChecked);
    const ingredientNames = uncheckedItems.map(item => item.name || item.original);

    // Find matches
    const matches = matchIngredientsToDeals(ingredientNames, allDeals);

    // Map back to grocery items with deal info
    return matches.map(match => ({
      groceryItem: uncheckedItems[match.ingredientIndex],
      deal: match.deal,
      confidence: match.confidence,
    }));
  }, [items, allDeals]);

  const handleToggleItem = (itemId: string) => {
    // Check if item is being checked ON before dispatching toggle
    const item = items.find((i: GroceryItem) => i.id === itemId);
    if (item) {
      const pantryName = shouldSyncToPantryOnToggle(item);
      if (pantryName) {
        dispatch(addItemsToPantryAndSync({ ingredientNames: [pantryName], userId: user?.uid }));
      }
    }
    dispatch(toggleItem(itemId));
  };

  const handleAddCustomItem = (name: string, category: GroceryCategory) => {
    dispatch(addCustomItem({ name, category }));
  };

  const handleRemoveCustomItem = (itemId: string) => {
    dispatch(removeCustomItem(itemId));
  };

  const handleClearRecipe = (recipeId: string) => {
    dispatch(removeRecipeItems(recipeId));
  };

  const handleMoveToInventory = (itemId: string) => {
    dispatch(moveToInventory(itemId));
  };

  // "Ranger les achats" — purchased items already entered the pantry when
  // checked; this archives them off the visible list.
  const handleStorePurchases = () => {
    if (checkedCount === 0) return;
    dispatch(clearPurchased());
    showToast(
      t('groceryList.storedPurchasesToast', {
        defaultValue: '{{count}} articles rangés au garde-manger',
        count: checkedCount,
      }),
      'success'
    );
  };

  const handleGoBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/(app)');
    }
  };

  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleClearAll = () => {
    setShowClearConfirm(true);
  };

  // Copy list to clipboard
  const handleCopyToClipboard = useCallback(async () => {
    // Web uses markdown format (* prefix), native uses plain newline-separated text
    const isWeb = Platform.OS === 'web';
    const format = isWeb ? 'markdown' : 'plain' as const;
    const { text: clipboardText, count } = formatGroceryListForCopy(items, alreadyHaveItems, format);

    if (!clipboardText) {
      showToast(t('groceryList.noItemsToCopy'), 'info');
      return;
    }

    try {
      if (isWeb && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(clipboardText);
        showToast(t('groceryList.copiedToClipboard', { count }), 'success');
      } else {
        const Clipboard = await import('expo-clipboard');
        await Clipboard.setStringAsync(clipboardText);
        showToast(t('groceryList.copiedToClipboard', { count }), 'success');
      }
      trackEvent('grocery_list_copied', { item_count: count });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback: show the text in an alert
      if (!isWeb) {
        Alert.alert(t('groceryList.title'), clipboardText);
      }
    }
  }, [items, alreadyHaveItems, t]);

  // Force refresh grocery list
  const handleForceRefresh = useCallback(() => {
    if (groceryWeekMeals.length === 0) return;
    dispatch(buildGroceryList());
  }, [dispatch, groceryWeekMeals.length]);

  // These two live up here, with the other hooks, and not inline on
  // <AlreadyHaveSection> where they started. The empty-state `return` below
  // means an inline hook is only reached on renders where the list has items,
  // so the hook count jumped 124 -> 126 the moment a list finished generating
  // and React tore the screen down mid-render. Every first generation hit it.
  const handleExcludeStaple = useCallback((stapleKey: string) => {
    dispatch(excludeStaple(stapleKey));
    dispatch(buildGroceryList());
  }, [dispatch]);

  const handleUndoExcludeStaple = useCallback((stapleKey: string) => {
    dispatch(unexcludeStaple(stapleKey));
    dispatch(buildGroceryList());
  }, [dispatch]);

  // Render a single grocery item row
  const renderGroceryItem = (item: GroceryItem, showRecipeAttribution = false) => {
    // Split quantity into amount + unit cells for tabular alignment
    let amountText = '';
    let unitText = '';
    if (item.amount && item.amount > 0) {
      amountText = formatQuantity(item.amount, item.unit);
      unitText = item.unit
        ? Platform.OS === 'web'
          ? item.unit
          : abbreviateUnit(item.unit)
        : '';
    } else {
      const quantityMatch = item.original?.match(/^([\d½¼¾⅓⅔⅛⅜⅝⅞\/\s.]+)\s*(\w+)?\s*/);
      if (quantityMatch) {
        amountText = (quantityMatch[1] || '').trim();
        unitText = (quantityMatch[2] || '').trim();
      }
    }

    const qtyText = [amountText, unitText].filter(Boolean).join(' ');

    return (
      <TouchableOpacity
        key={item.id}
        style={styles.itemRow}
        onPress={() => handleToggleItem(item.id)}
        onLongPress={() => handleMoveToInventory(item.id)}
        delayLongPress={500}
        activeOpacity={0.7}
      >
        {/* Purchase checkbox */}
        <View style={[styles.buyCheck, item.isChecked && styles.buyCheckChecked]}>
          {item.isChecked && <Feather name="check" size={15} color="#fff" />}
        </View>

        {/* Name. Masked while the ingredient text is still in the recipe's
            source language — showing "medium yellow onion" to a French user is
            worse than showing that something is on its way. The row itself
            stays: it counts toward the totals and can still be checked off,
            because an ingredient you have to buy must never be hidden. */}
        {item.pendingTranslation ? (
          <View
            style={styles.itemNamePending}
            accessibilityLabel={t('groceryList.translationPending')}
            testID="grocery-item-name-pending"
          />
        ) : (
          <Text
            style={[
              styles.itemName,
              item.isChecked && styles.itemNameChecked,
            ]}
            numberOfLines={3}
            // Named so QA can read the ingredient rows on their own. Scanning
            // body text instead picks up the recipe attribution underneath and
            // reports "poivre" on the buy list when the only "poivre" on screen
            // is the words "Steak au poivre".
            testID="grocery-item-name"
          >
            {item.name}
          </Text>
        )}

        {/* Provenance (which recipes need this) */}
        {showRecipeAttribution && item.recipeNames.length > 0 && (
          <Text style={styles.recipeAttribution} numberOfLines={1}>
            {item.recipeNames.join(' · ')}
          </Text>
        )}

        {/* Cumulated quantity */}
        {qtyText.length > 0 && (
          <Text
            style={[styles.itemQty, item.isChecked && styles.itemTextChecked]}
            numberOfLines={1}
          >
            {qtyText}
          </Text>
        )}

        {/* Delete button for custom items */}
        {item.isCustom && (
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => handleRemoveCustomItem(item.id)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Feather name="x" size={18} color={Paprika.textMuted} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    );
  };

  // Empty state
  if (items.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <Feather name="chevron-left" size={24} color={Paprika.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.title}>{t('groceryList.title')}</Text>
          {/* Keeps Settings reachable from the empty grocery list too (#531) */}
          <HeaderAvatarButton />
        </View>

        <View style={styles.emptyState}>
          <Feather name="shopping-cart" size={64} color={Paprika.textMuted} />
          <Text style={styles.emptyStateTitle}>{t('groceryList.noItemsYet')}</Text>
          <Text style={styles.emptyStateText}>
            {t('groceryList.addFromRecipes')}
          </Text>
          <TouchableOpacity
            style={styles.emptyStateButton}
            onPress={() => setIsAddModalVisible(true)}
          >
            <Feather name="plus" size={20} color="#FFFFFF" />
            <Text style={styles.emptyStateButtonText}>{t('groceryList.addFirstItem')}</Text>
          </TouchableOpacity>
        </View>

        <AddCustomItemModal
          visible={isAddModalVisible}
          onClose={() => setIsAddModalVisible(false)}
          onAdd={handleAddCustomItem}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Feather name="chevron-left" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('groceryList.title')}</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerActionButton} onPress={handleCopyToClipboard}>
            <Feather name="copy" size={20} color={Paprika.textPrimary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerActionButton} onPress={handleClearAll}>
            <Feather name="trash-2" size={20} color={Paprika.textPrimary} />
          </TouchableOpacity>
          <HeaderAvatarButton compact />
        </View>
      </View>

      {/* Add Item Button (at top) */}
      <TouchableOpacity
        style={styles.addItemButton}
        onPress={() => setIsAddModalVisible(true)}
        activeOpacity={0.7}
      >
        <Feather name="plus" size={20} color={Paprika.textPrimary} />
        <Text style={styles.addItemButtonText}>{t('groceryList.addAnItem')}</Text>
      </TouchableOpacity>

      {/* Serving Indicator & Sync Status */}
      <View style={styles.statusRow}>
        <View style={styles.servingIndicator}>
          <Feather name="users" size={14} color={Paprika.textSecondary} />
          <Text style={styles.servingText}>{t('groceryList.basedOnServings', { count: familySize })}</Text>
        </View>
        <SyncStatusIndicator onRefresh={handleForceRefresh} />
      </View>

      {/* View Toggle */}
      <View style={styles.viewToggleContainer}>
        <View style={styles.viewToggle}>
          <TouchableOpacity
            style={[styles.viewToggleOption, viewMode === 'recipe' && styles.viewToggleOptionActive]}
            onPress={() => setViewMode('recipe')}
          >
            <Text style={[styles.viewToggleText, viewMode === 'recipe' && styles.viewToggleTextActive]}>
              {t('groceryList.byRecipe')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.viewToggleOption, viewMode === 'category' && styles.viewToggleOptionActive]}
            onPress={() => setViewMode('category')}
          >
            <Text style={[styles.viewToggleText, viewMode === 'category' && styles.viewToggleTextActive]}>
              {t('groceryList.byCategory')}
            </Text>
          </TouchableOpacity>
          {lufaEnabled && (
            <TouchableOpacity
              style={[styles.viewToggleOption, viewMode === 'lufa' && styles.viewToggleOptionActive]}
              onPress={() => setViewMode('lufa')}
            >
              <Text style={[styles.viewToggleText, viewMode === 'lufa' && styles.viewToggleTextActive]}>
                Lufa {lufaItems.length > 0 ? `(${lufaItems.length})` : ''}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Content */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRegenerating}
            onRefresh={handleForceRefresh}
            tintColor={Paprika.accent}
            colors={[Paprika.accent]}
          />
        }
      >
        {/* AI banner — the list is built from the plan (from today on, #804), pantry items removed */}
        {(removedCount > 0 || pastDaysDropped) && (
          <View style={styles.aiBanner}>
            <View style={styles.aiBannerIcon}>
              <Feather name="zap" size={16} color={Paprika.ai} />
            </View>
            <View style={styles.aiBannerBody}>
              <Text style={styles.aiBannerText} testID="grocery-auto-banner">
                {(() => {
                  // Same order as the rows below, and one name per staple:
                  // two recipe lines can key to the same group ("sel" and
                  // "sel et poivre" are both salt), which would otherwise
                  // render as "Sel, Sel, Huile d'olive".
                  const stapleExamples = [
                    ...new Set(
                      alreadyHaveItems
                        .filter(i => i.source === 'staple' && i.stapleKey)
                        .map(i => i.stapleKey as string)
                    ),
                  ]
                    .sort((a, b) => stapleRank(a) - stapleRank(b))
                    .slice(0, 3)
                    .map(key => t(`groceryList.staples.${key}`))
                    .join(', ');

                  const pantryPart = pantryCount > 0
                    ? t('groceryList.autoBannerPantryPart', {
                        count: pantryCount,
                      })
                    : null;

                  const staplePart = stapleCount > 0
                    ? t('groceryList.autoBannerStaplePart', {
                        count: stapleCount,
                        examples: stapleExamples,
                      })
                    : null;

                  const lead = !pastDaysDropped
                    ? t('groceryList.autoBannerLead')
                    : groceryRange.start === groceryRange.end
                      ? t('groceryList.autoBannerLeadTodayOnly')
                      : t('groceryList.autoBannerLeadFromToday', {
                          end: formatWeekdayDay(groceryRange.end, i18n.language),
                        });

                  const removed = pantryPart && staplePart
                    ? t('groceryList.autoBannerRemovedBoth', { pantry: pantryPart, staples: staplePart })
                    : pantryPart || staplePart
                      ? t('groceryList.autoBannerRemovedOne', { part: pantryPart || staplePart })
                      : null;

                  return removed ? `${lead} ${removed}` : lead;
                })()}
              </Text>
              {!!autoSealLabel && <Text style={styles.aiBannerSeal}>{autoSealLabel}</Text>}
            </View>
          </View>
        )}

        {viewMode === 'lufa' && lufaEnabled ? (
          <>
            {/* Lufa Deals View */}
            {lufaItems.length === 0 ? (
              <View style={styles.emptyLufaState}>
                <Feather name="shopping-bag" size={48} color={Paprika.textMuted} style={styles.emptyLufaIcon} />
                <Text style={styles.emptyLufaTitle}>{t('groceryList.noLufaMatchesTitle')}</Text>
                <Text style={styles.emptyLufaText}>
                  {t('groceryList.noLufaMatchesText')}
                </Text>
              </View>
            ) : (
              <View style={styles.lufaSection}>
                <View style={styles.lufaSectionHeader}>
                  <Text style={styles.lufaSectionTitle}>{t('groceryList.availableAtLufa')}</Text>
                  <TouchableOpacity
                    style={styles.shopAllButton}
                    onPress={() => {
                      const url = 'https://montreal.lufa.com/fr/marche/categorie/produits-des-fermes-lufa/aubaines-de-la-semaine';
                      if (Platform.OS === 'web') {
                        window.open(url, '_blank');
                      } else {
                        import('react-native').then(({ Linking }) => Linking.openURL(url));
                      }
                    }}
                  >
                    <Text style={styles.shopAllButtonText}>{t('groceryList.shopAll')}</Text>
                    <Feather name="external-link" size={14} color="#2E7D32" />
                  </TouchableOpacity>
                </View>
                <View style={styles.lufaItemsList}>
                  {lufaItems.map((matched, index) => (
                    <TouchableOpacity
                      key={matched.groceryItem.id}
                      style={[
                        styles.lufaItemRow,
                        index === lufaItems.length - 1 && styles.lufaItemRowLast,
                      ]}
                      onPress={() => {
                        const url = matched.deal.productUrl || 'https://montreal.lufa.com/fr/marche/categorie/produits-des-fermes-lufa/aubaines-de-la-semaine';
                        if (Platform.OS === 'web') {
                          window.open(url, '_blank');
                        } else {
                          import('react-native').then(({ Linking }) => Linking.openURL(url));
                        }
                      }}
                      activeOpacity={0.7}
                    >
                      <View style={styles.lufaItemInfo}>
                        <Text style={styles.lufaItemName}>{matched.groceryItem.name}</Text>
                        <Text style={styles.lufaDealName} numberOfLines={1}>
                          {(i18n.language === 'fr' ? matched.deal.nameFr : matched.deal.name) || matched.deal.nameFr || matched.deal.name}
                        </Text>
                        {matched.groceryItem.recipeNames.length > 0 && (
                          <Text style={styles.lufaRecipeAttribution} numberOfLines={1}>
                            {t('groceryList.forRecipes', { recipes: matched.groceryItem.recipeNames.join(', ') })}
                          </Text>
                        )}
                      </View>
                      <View style={styles.lufaItemRight}>
                        {!!matched.deal.originalPrice && matched.deal.originalPrice > matched.deal.price && (
                          <View style={styles.lufaPriceContainer}>
                            <Text style={styles.lufaOriginalPrice}>
                              {formatMoney(matched.deal.originalPrice, i18n.language)}
                            </Text>
                            <Text style={styles.lufaSalePrice}>
                              {formatMoney(matched.deal.price, i18n.language)}
                            </Text>
                          </View>
                        )}
                        <Feather name="external-link" size={16} color={Paprika.textMuted} />
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </>
        ) : viewMode === 'recipe' ? (
          <>
            {/* Recipe Groups */}
            {Object.entries(itemsByRecipe.groups).map(([recipeId, group]) => (
              <View key={recipeId} style={styles.recipeSection}>
                <View style={styles.recipeSectionHeader}>
                  <View style={styles.recipeTitleRow}>
                    <Text style={styles.recipeSectionTitle} numberOfLines={1}>{group.recipeName}</Text>
                    {!!servingsByRecipe[recipeId] && (
                      <Text style={styles.recipeServingsLabel}>×{servingsByRecipe[recipeId]}</Text>
                    )}
                    <Text style={styles.recipeSectionCount}>
                      {group.items.filter(i => i.isChecked).length}/{group.items.length}
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={styles.clearRecipeButton}
                    onPress={() => handleClearRecipe(recipeId)}
                  >
                    <Text style={styles.clearRecipeButtonText}>{t('groceryList.clear')}</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.itemsList}>
                  {group.items.map(item => renderGroceryItem(item, false))}
                </View>
              </View>
            ))}

            {/* Other Items (manual additions) */}
            {itemsByRecipe.otherItems.length > 0 && (
              <View style={styles.recipeSection}>
                <View style={styles.recipeSectionHeader}>
                  <Text style={styles.recipeSectionTitle}>{t('groceryList.otherItems')}</Text>
                </View>
                <View style={styles.itemsList}>
                  {itemsByRecipe.otherItems.map(item => renderGroceryItem(item, false))}
                </View>
              </View>
            )}
          </>
        ) : (
          <>
            {/* Rayon groups (French aisles) */}
            {itemsByRayon.map(({ rayon, items: rayonItems }) => (
              <View key={rayon.key} style={styles.categorySection}>
                <View style={styles.categorySectionHeader}>
                  <Text style={styles.rayonTitle}>{t(rayon.labelKey, rayon.defaultLabel)}</Text>
                  <Text style={styles.categorySectionCount}>{rayonItems.length}</Text>
                </View>
                <View style={styles.itemsList}>
                  {rayonItems.map((item: GroceryItem) => renderGroceryItem(item, true))}
                </View>
              </View>
            ))}
          </>
        )}

        {/* Already Have Section */}
        <AlreadyHaveSection
          items={alreadyHaveItems}
          onExcludeStaple={handleExcludeStaple}
          onUndoExcludeStaple={handleUndoExcludeStaple}
        />

        {/* Bottom spacing */}
        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Footer — purchased count + "Ranger les achats" */}
      <View style={styles.footer}>
        <Text style={styles.footerCount}>
          <Text style={styles.footerCountStrong}>{checkedCount}</Text>
          {` ${t('groceryList.footerTaken', 'pris')} · `}
          <Text style={styles.footerCountStrong}>{remainingCount}</Text>
          {` ${t('groceryList.footerRemaining', 'restants')}`}
        </Text>
        <TouchableOpacity
          style={[styles.storeButton, checkedCount === 0 && styles.storeButtonDisabled]}
          onPress={handleStorePurchases}
          disabled={checkedCount === 0}
          activeOpacity={0.85}
        >
          <Feather name="archive" size={16} color="#fff" />
          <Text style={styles.storeButtonText}>
            {t('groceryList.storePurchases', 'Ranger les achats')}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Add Custom Item Modal */}
      <AddCustomItemModal
        visible={isAddModalVisible}
        onClose={() => setIsAddModalVisible(false)}
        onAdd={handleAddCustomItem}
      />

      {/* Clear Confirmation Modal */}
      <ConfirmationModal
        visible={showClearConfirm}
        title={t('groceryList.clearListTitle')}
        message={t('groceryList.clearListMessage', { count: items.length })}
        confirmLabel={t('groceryList.clearAll')}
        destructive
        onConfirm={() => {
          dispatch(clearList());
          setShowClearConfirm(false);
        }}
        onCancel={() => setShowClearConfirm(false)}
      />

      {/* Toast */}
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onDismiss={() => setToastVisible(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: Platform.OS === 'ios' ? 60 : Platform.OS === 'web' ? 16 : 40,
    paddingBottom: PaprikaSpacing.md,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerActionButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Add Item Button (at top)
  addItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: PaprikaSpacing.lg,
    marginTop: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.sm,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.lg,
    gap: 8,
  },
  addItemButtonText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },

  // Status row (serving indicator + sync status)
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.sm,
  },
  servingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  servingText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },

  // View Toggle
  viewToggleContainer: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.md,
  },
  viewToggle: {
    flexDirection: 'row',
    backgroundColor: Paprika.surfaceLight,
    borderRadius: PaprikaRadius.md,
    padding: 4,
  },
  viewToggleOption: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: PaprikaRadius.sm,
  },
  viewToggleOptionActive: {
    backgroundColor: '#FFFFFF',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      },
    }),
  },
  viewToggleText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
  viewToggleTextActive: {
    color: Paprika.textPrimary,
    fontWeight: '600',
  },

  // Content
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: Platform.OS === 'web' ? 100 : 40, // Extra padding for PWA standalone mode
  },

  // Recipe Section
  recipeSection: {
    marginBottom: PaprikaSpacing.lg,
  },
  recipeSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: PaprikaSpacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  recipeTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  recipeSectionTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    flexShrink: 1,
  },
  recipeServingsLabel: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
    fontWeight: '600',
  },
  recipeSectionCount: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
  },
  clearRecipeButton: {
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: 4,
  },
  clearRecipeButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },

  // Category Section
  categorySection: {
    marginBottom: PaprikaSpacing.lg,
  },
  categorySectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: PaprikaSpacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  categorySectionTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  rayonTitle: {
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  categorySectionCount: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontVariant: ['tabular-nums'],
  },

  // AI banner
  aiBanner: {
    flexDirection: 'row',
    gap: 12,
    backgroundColor: Paprika.aiTint,
    borderRadius: 15,
    padding: 13,
    marginTop: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.md,
  },
  aiBannerIcon: {
    width: 36,
    height: 36,
    borderRadius: 11,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  aiBannerBody: {
    flex: 1,
    minWidth: 0,
  },
  aiBannerText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    lineHeight: 18,
  },
  aiBannerStrong: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  aiBannerSeal: {
    ...PaprikaTypography.caption,
    color: Paprika.ai,
    marginTop: 3,
  },

  // Items List
  itemsList: {
    // Container for item rows
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  buyCheck: {
    width: 26,
    height: 26,
    borderRadius: 9,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    backgroundColor: Paprika.cardBg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buyCheckChecked: {
    backgroundColor: Paprika.success,
    borderColor: Paprika.success,
  },
  itemName: {
    ...PaprikaTypography.bodyMedium,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
    flex: 1,
    minWidth: 72,
  },
  // Deliberately static, not a shimmer. RN Web already logs useNativeDriver
  // warnings here, and a looping animation on every row of a long list is not
  // worth the jank for a state that lasts a couple of seconds.
  itemNamePending: {
    flex: 1,
    minWidth: 72,
    height: 14,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.surfaceHighest,
  },
  itemNameChecked: {
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    color: Paprika.textMuted,
    textDecorationLine: 'line-through',
  },
  itemTextChecked: {
    color: Paprika.textMuted,
  },
  recipeAttribution: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    maxWidth: 110,
    flexShrink: 1,
  },
  itemQty: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    fontVariant: ['tabular-nums'],
    minWidth: 44,
    textAlign: 'right',
  },
  deleteButton: {
    padding: 8,
    marginLeft: 8,
  },

  // Footer — purchased count + store button
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.sm,
    paddingBottom: Platform.OS === 'web' ? PaprikaSpacing.md : PaprikaSpacing.lg,
    backgroundColor: Paprika.cardBg,
    borderTopWidth: 1,
    borderTopColor: Paprika.borderLight,
  },
  footerCount: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  footerCountStrong: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
    fontVariant: ['tabular-nums'],
  },
  storeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.toastBg,
    borderRadius: PaprikaRadius.full,
    paddingVertical: 12,
    paddingHorizontal: 18,
  },
  storeButtonDisabled: {
    opacity: 0.35,
  },
  storeButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    fontSize: 13.5,
    color: '#fff',
  },

  // Empty State
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
  },
  emptyStateTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginTop: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.sm,
  },
  emptyStateText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.xl,
  },
  emptyStateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Paprika.accent,
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.lg,
    borderRadius: PaprikaRadius.md,
    gap: 8,
  },
  emptyStateButtonText: {
    ...PaprikaTypography.labelMedium,
    color: '#FFFFFF',
    fontWeight: '600',
  },

  // Lufa View Styles
  emptyLufaState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  emptyLufaIcon: {
    marginBottom: PaprikaSpacing.md,
  },
  emptyLufaTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.sm,
  },
  emptyLufaText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  lufaSection: {
    marginBottom: PaprikaSpacing.lg,
  },
  lufaSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: PaprikaSpacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  lufaSectionTitle: {
    ...PaprikaTypography.titleMedium,
    color: '#2E7D32',
    fontWeight: '600',
  },
  shopAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: PaprikaRadius.full,
    gap: 4,
  },
  shopAllButtonText: {
    ...PaprikaTypography.labelMedium,
    color: '#2E7D32',
    fontWeight: '600',
  },
  lufaItemsList: {
    backgroundColor: '#FFFFFF',
  },
  lufaItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  lufaItemRowLast: {
    borderBottomWidth: 0,
  },
  lufaItemInfo: {
    flex: 1,
    marginRight: PaprikaSpacing.sm,
  },
  lufaItemName: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    fontWeight: '500',
  },
  lufaDealName: {
    ...PaprikaTypography.caption,
    color: '#2E7D32',
    marginTop: 2,
  },
  lufaRecipeAttribution: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: 2,
    fontStyle: 'italic',
  },
  lufaItemRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  lufaPriceContainer: {
    alignItems: 'flex-end',
  },
  lufaOriginalPrice: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    textDecorationLine: 'line-through',
  },
  lufaSalePrice: {
    ...PaprikaTypography.bodyMedium,
    color: '#2E7D32',
    fontWeight: '600',
  },
});

export default GroceryListScreen;
